/* eslint-disable jsx-a11y/click-events-have-key-events,jsx-a11y/no-static-element-interactions */
import React, { Fragment } from 'react';
import {
  arrayOf,
  string,
  shape,
  func,
  number,
} from 'prop-types';
import qs from 'query-string';
import styleVariables from 'styles/variables';
import scrollIntoView from 'scroll-into-view';
import _ from 'lodash';
import { isEmpty } from 'ramda';
import moment from 'moment';
import withApplicationConfig from 'containers/withApplicationConfig';
import { withRouter } from 'react-router';
import { withTracking } from 'components/tracking';
import { getTiFunnelTripDateFormat } from 'helpers/DateFormatUtil';
import {
  InlineInput,
  Button,
  Paragraph,
  DatePicker,
} from 'components/common';
import { validateDob } from 'helpers/ValidationUtil';
import { ImageAndLinkBanner, TravelInsuranceFunnelLanding } from '@hyphengroup/cag-components';
import FunnelInputGroup from './FunnelInputGroup';
import DobPanel from './DobPanel';
import RelationshipPanel from './RelationshipPanel';
import SelectPanel from './SelectPanel';
import FunnelSelectionButton from './FunnelSelectionButton';
import FunnelTip from './FunnelTip';
import TravelInsuranceFunnelRightSide from './TravelInsuranceFunnelRightSide';
import {
  getValidDestinationsAndPlaceholderValue,
  getValidTravellersAndPlaceholderValue,
  getValidTravellersAndPlaceholderValueAfterDelete,
  toggleAdditionalCoverages,
  validateInsuranceType,
  validateRegion,
  validateDestinations,
  validateTravellers,
  validateStartDate,
  validateEndDate,
  validateStartDateAndEndDate,
  getDestinationSpecificTips,
} from './helpers';
import {
  travelInsuranceNextButtonTrack,
  onTypeChangeTrack,
} from './FunnelTrackingUtils';

import './TravelInsuranceFunnel.scss';
import {parseAssetString} from '../../helpers/Utils';

const DATE_FORMAT = 'YYYY-MM-DD';

const INSURANCE_TYPE_ENUM = {
  SINGLE_PLAN: 'SINGLE_PLAN',
  ANNUAL_PLAN: 'ANNUAL_PLAN',
};

const RELATIONSHIP_ENUM = {
  SELF: 'SELF',
  SPOUSE: 'SPOUSE',
  CHILD: 'CHILD',
  COMPANION: 'COMPANION',
};

const STEP_ENUM = {
  INSURANCE_TYPE: 'INSURANCE_TYPE',
  DESTINATIONS: 'DESTINATIONS',
  TRAVELLERS: 'TRAVELLERS',
  START_DATE_AND_END_DATE: 'START_DATE_AND_END_DATE',
  START_DATE: 'START_DATE',
  COVERAGES: 'COVERAGES',
  REGION: 'REGION',
};

class TravelInsuranceFunnel extends React.Component {
  constructor(props) {
    super(props);

    // Get initial state from props if any
    const {
      initialFunnelQuery,
      insuranceTypes,
      countryOptions,
      singlePlanRelationashipSelections,
      annualPlanRelationashipSelections,
      coverageSelections,
      rightSideImg,
      singlePlanSteps,
      annualPlanSteps,
      regions,
    } = props;

    this.state = {
      activeIndex: 0,
      currentIndex: 0,
      isValid: false,
      isOneOfDestinationInputsFocused: false,
      isMobile: false,

      // Form Data
      insuranceType: null,
      region: '',
      destinations: [{}],
      isDestinationInputsClean: true,
      travellers: [{
        // TODO: default use single plan, should be fine but add a todo anyway
        relationship: _.first(singlePlanRelationashipSelections),
      }],
      rightSideImg,
      startDate: null,
      endDate: null,
      additionalCoverages: [],
      forceDobPanelShowError: false,
    };

    if (initialFunnelQuery) {
      const {
        insuranceType,
        destinations,
        travellers,
        startDate,
        region,
        endDate,
        additionalCoverages,
      } = initialFunnelQuery;

      // Get steps based on insurance type
      const steps = insuranceType === INSURANCE_TYPE_ENUM.ANNUAL_PLAN
        ? annualPlanSteps
        : singlePlanSteps;

      // Get relationship selections based on insurance type
      const relationshipSelections = insuranceType === INSURANCE_TYPE_ENUM.ANNUAL_PLAN
        ? annualPlanRelationashipSelections
        : singlePlanRelationashipSelections;

      // Assume in here all steps has been gone through
      this.state.currentIndex = steps.length - 1;
      this.state.activeIndex = steps.length - 1;
      this.state.isValid = true;

      this.state.insuranceType = _.find(insuranceTypes, { value: insuranceType });
      this.state.region = _.find(regions, { value: region });

      const travellersWithValidation = travellers
        .map(({ relationship, dateOfBirth }) => ({
          relationship: {
            label: _.find(relationshipSelections, { value: relationship }).label,
            value: relationship,
          },
          dateOfBirth,
          dateOfBirthValid: validateDob(
            moment(dateOfBirth),
            null,
            null,
            this.getAgeLimitsFromRelationship({ value: relationship }).min,
            this.getAgeLimitsFromRelationship({ value: relationship }).max
          ).isValidDate,
        }));
      !isEmpty(this.getUnusedRelationshipSelections(travellersWithValidation))
      && travellersWithValidation.push({});

      switch (insuranceType) {
        case INSURANCE_TYPE_ENUM.SINGLE_PLAN:
          this.state.destinations = destinations
            .map(destination => ({
              value: destination,
              label: _.find(countryOptions.entries, { value: destination }).label,
              isValueFromSuggestion: true,
            }))
            .concat({});

          this.state.isDestinationInputsClean = !destinations.length;
          this.state.travellers = travellersWithValidation;
          this.state.startDate = startDate;
          this.state.endDate = endDate;

          this.state.additionalCoverages = additionalCoverages
            ?.map(coverage => ({
              value: coverage,
              label: _.find(coverageSelections, { value: coverage }).label,
            })) || [];
          break;
        case INSURANCE_TYPE_ENUM.ANNUAL_PLAN:
          this.state.travellers = travellersWithValidation;

          this.state.startDate = startDate;

          this.state.additionalCoverages = additionalCoverages
            ?.map(coverage => ({
              value: coverage,
              label: _.find(coverageSelections, { value: coverage }).label,
            })) || [];
          break;
        default:
          break;
      }
    }

    this.onTypeSelectPanelChange = this.onTypeSelectPanelChange.bind(this);

    this.onNextButtonClick = this.onNextButtonClick.bind(this);

    this.onInsuranceTypeChange = this.onInsuranceTypeChange.bind(this);
    this.onRegionChange = this.onRegionChange.bind(this);

    this.onDestinationChange = this.onDestinationChange.bind(this);
    this.onDestinationFocus = this.onDestinationFocus.bind(this);
    this.onDestinationBlur = this.onDestinationBlur.bind(this);
    this.onFeaturedDestinationSuggestionClick = this
      .onFeaturedDestinationSuggestionClick.bind(this);
    this.onRemoveDestination = this.onRemoveDestination.bind(this);

    this.onRelationshipChange = this.onRelationshipChange.bind(this);
    this.onDateOfBirthChange = this.onDateOfBirthChange.bind(this);
    this.onDateOfBirthValidate = this.onDateOfBirthValidChange.bind(this);
    this.onRemoveTraveller = this.onRemoveTraveller.bind(this);
    this.onRemoveTravellerWithoutDob = this.onRemoveTravellerWithoutDob.bind(this);
    this.onCustomPanelOutsideClick = this.onCustomPanelOutsideClick.bind(this);

    this.onCoverageSelectionButtonClick = this.onCoverageSelectionButtonClick.bind(this);

    this.validate = this.validate.bind(this);

    this.mobileMediaQueryHandler = this.mobileMediaQueryHandler.bind(this);
    this.inputGroupWrapperEls = {};
    this.inputGroupWrapperRefCallback = this.inputGroupWrapperRefCallback.bind(this);

    this.getAgeLimitsFromRelationship = this.getAgeLimitsFromRelationship.bind(this);
    this.getDOBPanelMessages = this.getDOBPanelMessages.bind(this);

    this.onSinglePlanStartDateChange = this.onSinglePlanStartDateChange.bind(this);

    this.inlineInputEls = {};
    this.inlineInputRefCallback = this.inlineInputRefCallback.bind(this);
  }

  static getDerivedStateFromProps({
    match: { params }, insuranceTypes, tiRoutes, regions,
  }, {
    currentIndex: prevCurrentIndex,
    list: prevList,
    region,
  }) {
    // get list from url and put it to insurance type
    const { list } = params;
    if (prevList && prevList === list) return null;
    return {
      insuranceType: null,
      list,
      ...list && tiRoutes[list] && {
        insuranceType: _.find(insuranceTypes, { value: tiRoutes[list] }),
        currentIndex: prevCurrentIndex === 0 ? 1 : prevCurrentIndex,
        activeIndex: prevCurrentIndex === 0 ? 1 : prevCurrentIndex,
        region: region ? _.find(regions, region) : '',
      },
    };
  }

  componentDidMount() {
    // JS mobile media query
    this.mobileMediaQuery = window.matchMedia(`(max-width: ${styleVariables.screenXsMax}px)`);
    this.mobileMediaQuery.addListener(this.mobileMediaQueryHandler);
    this.setState({
      isMobile: this.mobileMediaQuery.matches,
    });

    // Validate when accessing from nav bar with list
    const { currentIndex } = this.state;
    if (currentIndex > 0) {
      this.validate();
    }
  }

  componentDidUpdate(prevProps, {
    insuranceType: prevInsuranceType,
  }) {
    const { insuranceType } = this.state;

    // Handle validation after history push
    if (prevInsuranceType !== insuranceType) {
      this.validate();
    }
  }

  componentWillUnmount() {
    if (this.mobileMediaQuery) {
      this.mobileMediaQuery.removeListener(this.mobileMediaQueryHandler);
    }
  }

  onTypeSelectPanelChange({ label, value }) {
    this.onInsuranceTypeChange({ label, value });
  }

  onNextButtonClick() {
    const {
      currentIndex,
      insuranceType,
      region,
      destinations,
      travellers,
      startDate,
      endDate,
      additionalCoverages,
    } = this.state;
    const {
      onSubmit,
      singlePlanSteps,
      annualPlanSteps,
    } = this.props;

    // Get steps based on insurance type
    const steps = (insuranceType || {}).value === INSURANCE_TYPE_ENUM.ANNUAL_PLAN
      ? annualPlanSteps
      : singlePlanSteps;

    if (currentIndex < steps.length - 1) {
      this.setState({
        currentIndex: currentIndex + 1,
        activeIndex: currentIndex + 1,
      }, this.validate);

      // If mobile scroll to next input
      const { isMobile } = this.state;
      if (isMobile) {
        const nextStep = steps[currentIndex + 1];
        if (this.inputGroupWrapperEls[nextStep]) {
          scrollIntoView(this.inputGroupWrapperEls[nextStep], {
            time: 300,
            align: { top: 0.1 },
          });
        }
      }
    } else if (onSubmit) {
      switch ((insuranceType || {}).value) {
        case INSURANCE_TYPE_ENUM.SINGLE_PLAN:
          // submit single plan funnel query
          onSubmit({
            insuranceType: (insuranceType || {}).value,
            destinations: destinations
              .filter(destination => !_.isEmpty(destination))
              .map(({ value }) => value),
            travellers: travellers
              .filter(traveller => !_.isEmpty(traveller))
              .map(({ relationship: { value }, dateOfBirth }) => ({
                relationship: value,
                dateOfBirth,
              })),
            startDate,
            endDate,
            additionalCoverages: additionalCoverages.map(({ value }) => value),
          });
          break;
        case INSURANCE_TYPE_ENUM.ANNUAL_PLAN:
          // submit annual plan funnel query
          onSubmit({
            insuranceType: (insuranceType || {}).value,
            region: (region || {}).value,
            travellers: travellers
              .filter(traveller => !_.isEmpty(traveller))
              .map(({ relationship: { value }, dateOfBirth }) => ({
                relationship: value,
                dateOfBirth,
              })),
            startDate,
            additionalCoverages: additionalCoverages.map(({ value }) => value),
          });
          break;
        default:
          break;
      }
    }
    const funnelData = {
      insuranceType: (insuranceType || {}).value,
      region: (region || {}).value,
      destinations: destinations
        .filter(destination => !_.isEmpty(destination))
        .map(({ value }) => value),
      travellers: travellers
        .filter(traveller => !_.isEmpty(traveller))
        .map(({ relationship: { value }, dateOfBirth }) => ({
          relationship: value,
          dateOfBirth,
        })),
      startDate,
      endDate,
      additionalCoverages: additionalCoverages.map(({ value }) => value),
    };
    /* Tracking */
    travelInsuranceNextButtonTrack(this.props, currentIndex, steps, STEP_ENUM, funnelData);
  }

  onInsuranceTypeChange({ value }) {
    const {
      singlePlanRelationashipSelections,
      annualPlanRelationashipSelections,
    } = this.props;

    const { insuranceType } = this.state;

    if (insuranceType && insuranceType.value === value) {
      return;
    }

    // Get relationship selections based on insurance type
    const relationshipSelections = value === INSURANCE_TYPE_ENUM.ANNUAL_PLAN
      ? annualPlanRelationashipSelections
      : singlePlanRelationashipSelections;

    // Reset to initial state except insurance type
    this.setState({
      currentIndex: 0,
      activeIndex: 0,
      destinations: [{}],
      isDestinationInputsClean: true,
      travellers: [{
        relationship: _.first(relationshipSelections),
      }],
      startDate: null,
      endDate: null,
      additionalCoverages: [],
      region: null,
    }, () => {
      // push list which represents type to history
      const {
        history, location, tiRoutes, funnelPathname,
      } = this.props;
      const regex = new RegExp(`${funnelPathname}.*$`);
      const searchParams = qs.parse(location.search);
      delete searchParams.funnelQuery;
      history.push({
        pathname: location.pathname.toLowerCase().replace(regex, `${funnelPathname}/${tiRoutes[value]}`),
        search: qs.stringify(searchParams),
      });
    });

    /* Tracking */
    onTypeChangeTrack(this.props, { travelType: value });
  }

  onRegionChange({ value, label }) {
    const { region } = this.state;
    if (region && region.value === value) {
      return;
    }
    this.setState({ region: { value, label } }, this.validate);
    /* Tracking */
    onTypeChangeTrack(this.props, { region: value });
  }

  onDestinationChange(i) {
    return ({ label, value, isValueFromSuggestion }) => {
      const { destinations } = this.state;
      destinations[i] = {
        label,
        value,
        isValueFromSuggestion,
      };

      this.setState({ destinations }, this.validate);
    };
  }

  onDestinationFocus() {
    return () => {
      this.setState({
        isOneOfDestinationInputsFocused: true,
        isDestinationInputsClean: false,
      });
    };
  }

  onDestinationBlur() {
    return () => {
      const { destinations } = this.state;

      this.setState({
        destinations: getValidDestinationsAndPlaceholderValue(destinations),
        isOneOfDestinationInputsFocused: false,
      }, this.validate);
    };
  }

  onRemoveDestination(i) {
    return () => {
      const { destinations } = this.state;
      destinations[i] = {
        value: '',
      };

      this.setState({
        destinations: getValidDestinationsAndPlaceholderValue(destinations),
        isOneOfDestinationInputsFocused: false,
      }, this.validate);
    };
  }

  onFeaturedDestinationSuggestionClick({ label, value }) {
    return () => {
      const { destinations } = this.state;
      destinations.push({
        label,
        value,
        isValueFromSuggestion: true,
      });

      this.setState({
        isDestinationInputsClean: false,
        destinations: getValidDestinationsAndPlaceholderValue(destinations),
      }, this.validate);
    };
  }

  onRelationshipChange(i) {
    return ({ label, value }) => {
      const { travellers } = this.state;
      travellers[i].relationship = { label, value };
      this.setState({
        travellers,
      }, this.validate);
    };
  }

  onDateOfBirthChange(i, unusedRelationshipSelections) {
    return (value) => {
      const { travellers } = this.state;
      travellers[i].dateOfBirth = value;
      this.setState({
        travellers: getValidTravellersAndPlaceholderValue(travellers,
          !isEmpty(unusedRelationshipSelections)),
      }, this.validate);
    };
  }

  onDateOfBirthValidChange(i, unusedRelationshipSelections) {
    return (value) => {
      const { travellers } = this.state;
      travellers[i].dateOfBirthValid = value;
      if (this.inputGroupWrapperEls[STEP_ENUM.TRAVELLERS]) {
        this.setState({
          travellers:
            getValidTravellersAndPlaceholderValue(travellers,
              !isEmpty(unusedRelationshipSelections)),
        }, this.validate);
      }
    };
  }

  onRemoveTraveller(i) {
    return () => {
      const { travellers } = this.state;
      travellers[i] = {};
      this.setState({
        travellers: getValidTravellersAndPlaceholderValueAfterDelete(travellers),
      }, this.validate);
    };
  }

  onRemoveTravellerWithoutDob(i) {
    return () => {
      const { travellers } = this.state;
      if (travellers[i]
        && !travellers[i].dateOfBirth
        && _.get(travellers, `[${i}].relationship.value`) !== RELATIONSHIP_ENUM.SELF) {
        travellers[i] = {};
      }

      this.setState({
        travellers: getValidTravellersAndPlaceholderValueAfterDelete(travellers),
      }, this.validate);
    };
  }

  onCoverageSelectionButtonClick({ label, value }) {
    return () => {
      const { additionalCoverages } = this.state;
      this.setState({
        additionalCoverages: toggleAdditionalCoverages(additionalCoverages, { label, value }),
      }, this.validate);
    };
  }

  onCustomPanelOutsideClick(i) {
    return () => {
      const { travellers } = this.state;
      if (travellers[i].dateOfBirthValid) {
        this.inputGroupWrapperEls[STEP_ENUM.TRAVELLERS].querySelector('.done-button-wrapper .text-button').click();
      } else {
        this.setState({ forceDobPanelShowError: true });
      }
    };
  }

  onSinglePlanStartDateChange(date) {
    const { endDate } = this.state;
    const endDateMoment = moment(endDate);
    const shouldSetEndDate = endDateMoment.isValid && endDateMoment < date;
    const formattedDate = date.format(DATE_FORMAT);
    this.setState({
      startDate: formattedDate,
      ...shouldSetEndDate && ({ endDate: formattedDate }),
    });
  }

  getAgeLimitsFromRelationship({ value }) {
    const { ageLimits = {} } = this.props;
    return ageLimits[value] || ageLimits.DEFAULT;
  }

  getDOBPanelMessages({ value }) {
    const { ageLimits = {} } = this.props;
    return ageLimits[value] || ageLimits.DEFAULT;
  }

  allTravelersAreValid = () => {
    let valid = true;
    const { travellers } = this.state;
    travellers.forEach((t) => {
      if (Object.keys(t).length > 0) {
        valid = valid && t.dateOfBirthValid === true && t.relationship != null;
      }
    });
    return valid;
  };

  mobileMediaQueryHandler(e) {
    this.setState({
      isMobile: e.matches,
    });
  }

  funnelInputGroupClickHandler(e, i) {
    if (!this.inputGroupWrapperEls[STEP_ENUM.TRAVELLERS].querySelector('.error-message-wrapper')) {
      this.setState({ activeIndex: i });
    }
  }

  inputGroupWrapperRefCallback(step) {
    return (el) => {
      this.inputGroupWrapperEls[step] = el;
    };
  }

  inlineInputRefCallback(key) {
    return (el) => {
      this.inlineInputEls[key] = el;
    };
  }

  validate() {
    const {
      singlePlanEndDayLimit,
      singlePlanStartDayLimit,
      annualPlanStartDayLimit,
      singlePlanSteps,
      annualPlanSteps,
    } = this.props;
    const {
      insuranceType,
      currentIndex,
      destinations,
      travellers,
      startDate,
      endDate,
      region,
    } = this.state;
    if (!insuranceType) {
      return;
    }
    const validationMapping = {
      INSURANCE_TYPE: validateInsuranceType(insuranceType),
      DESTINATIONS: validateDestinations(destinations),
      TRAVELLERS: validateTravellers(travellers),
      START_DATE_AND_END_DATE: validateStartDateAndEndDate(
        startDate, endDate, singlePlanStartDayLimit, singlePlanEndDayLimit
      ),
      REGION: validateRegion(region),
      START_DATE: validateStartDate(startDate, annualPlanStartDayLimit),
      COVERAGES: true,
    };
    const singlePlanValidation = singlePlanSteps.map(step => validationMapping[step]);
    const annualPlanValidation = annualPlanSteps.map(step => validationMapping[step]);
    const rules = insuranceType.value === INSURANCE_TYPE_ENUM.ANNUAL_PLAN
      ? annualPlanValidation
      : singlePlanValidation;
    // Reduce validation rules based on current step
    const isValid = rules.slice(0, currentIndex + 1).reduce((a, b) => a && b);
    this.setState({ isValid });
  }

  getRelationshipSelections = () => {
    const {
      insuranceType,
    } = this.state;

    const {
      singlePlanRelationashipSelections,
      annualPlanRelationashipSelections,
    } = this.props;

    return ((insuranceType || {}).value === INSURANCE_TYPE_ENUM.ANNUAL_PLAN
      ? annualPlanRelationashipSelections.slice(1)
      : singlePlanRelationashipSelections.slice(1));
  }

  getUnusedRelationshipSelections = travellers => {
    const relationshipSelections = this.getRelationshipSelections();
    return relationshipSelections
      .filter(({ value }) => {
        const relationshipCount = travellers
          .filter(({ relationship }) => !_.isEmpty(relationship))
          .filter(({
            relationship: {
              value: relationshipValue,
            },
          }) => relationshipValue === value)
          .length;

        switch (value) {
          case RELATIONSHIP_ENUM.SPOUSE:
            return relationshipCount < 1;
          case RELATIONSHIP_ENUM.CHILD:
            return relationshipCount < 9;
          case RELATIONSHIP_ENUM.COMPANION:
            return relationshipCount < 6;
          default:
            return true;
        }
      });
  }

  render() {
    const {
      activeIndex,
      currentIndex,
      insuranceType,
      region,
      destinations,
      travellers,
      isOneOfDestinationInputsFocused,
      startDate,
      endDate,
      additionalCoverages,
      isValid,
      rightSideImg,
      isDestinationInputsClean,
      forceDobPanelShowError,
    } = this.state;

    const {
      // App config
      localisation,
      language,
      singlePlanEndDayLimit,
      singlePlanStartDayLimit,
      annualPlanStartDayLimit,
      dictionary = {},
      typeSelectionPanel = {},
      sentences = {},
      tips = {},
      inputPlaceholders = {},
      inputErrorMsg = {},
      insuranceTypes,
      countryOptions,
      featuredDestinationSuggestions,
      coverageSelections,
      singlePlanSteps,
      annualPlanSteps,
      regions,
      excludedDestinations,
      providerImages,
      promotionalBanners,
      topBanner: { desktopImageKey, mobileImageKey },
      onPromotionalBannerClick,
      faqs,
      tiShortDisclaimer,
      tiLongDisclaimer,
      video,
      latestNews,
      topBanner,
    } = this.props;

    const locale = localisation[language];
    // Set react dates locale
    moment.locale(locale);

    const destinationValues = destinations
      .filter(destination => !_.isEmpty(destination))
      .map(({ value }) => value);
    const unusedFeaturedDestinationSuggestions = featuredDestinationSuggestions
      .filter(({ value }) => !destinationValues.includes(value));
    const allowedDestinationSuggestions = countryOptions.entries
      .filter(({ value }) => !excludedDestinations.includes(value));
    const unusedDestinationSuggestions = allowedDestinationSuggestions
      .filter(({ value }) => !destinationValues.includes(value));
    const isShowDestinationInputsError = !validateDestinations(destinations)
      && !isDestinationInputsClean
      && !isOneOfDestinationInputsFocused;

    // Shortlist relationship selection after existing relationship counts exceed
    // certain amount
    const unusedRelationshipSelections = this.getUnusedRelationshipSelections(travellers);

    // Get steps based on insurance type
    const steps = (insuranceType || {}).value === INSURANCE_TYPE_ENUM.ANNUAL_PLAN
      ? annualPlanSteps
      : singlePlanSteps;

    // Go to type selection panel if no insurance type
    if (!insuranceType) {
      return (
        <TravelInsuranceFunnelLanding
          topBanner={{
            desktopImageKey: topBanner?.desktopImageKey,
            mobileImageKey: topBanner?.mobileImageKey,
            promotionalBanners,
          }}
          travelInsuranceTypeSelectionPanelProps={{
            typeSelectionPanel: {
              backgroundImageUrl: typeSelectionPanel?.backgroundImageUrl,
              header: typeSelectionPanel?.header,
              bottomTip: typeSelectionPanel?.bottomTip,
            },
            insuranceTypes,
            sentences,
            onChange: this.onTypeSelectPanelChange,
          }}
          providerPanel={{
            title: typeSelectionPanel?.providersPanelHeader,
            providerImageList: (providerImages || []).map(item => ({
              src: parseAssetString({
                rawJson: item.rawJson,
                property: 'url',
                uriEncode: true,
                dimensions: typeSelectionPanel?.providerDimensions,
              }),
              alt: item.title,
            })),
          }}
          tiShortDisclaimer={tiShortDisclaimer}
          videoPanel={video}
          blogPanel={latestNews ? {
            title: latestNews.title,
            description: latestNews.subheading,
            posts: latestNews.blogArticles?.map((blogArticle) => ({
              image: {
                src: blogArticle?.image?.url,
              },
              title: blogArticle?.title,
              description: blogArticle?.description,
              href: blogArticle?.link,
            })),
            seeAll: {
              text: latestNews?.viewMoreCta?.title,
              href: latestNews?.viewMoreCta?.href,
            },
          } : null}
          faqPanel={faqs}
          tiLongDisclaimer={tiLongDisclaimer}
        />
      );
    }

    return (
      <Fragment>
        <div
          data-selector-type="promotional-banner"
          onClick={onPromotionalBannerClick}
        >
          <ImageAndLinkBanner
            desktopImageKey={desktopImageKey}
            mobileImageKey={mobileImageKey}
            promotionalBanners={promotionalBanners}
          />
        </div>
        <div className="ti-funnel">
          <TravelInsuranceFunnelRightSide
            steps={steps}
            activeIndex={activeIndex}
            currentIndex={currentIndex}
            insuranceType={insuranceType}
            destinations={destinations}
            travellers={travellers}
            rightSideImg={rightSideImg}
          />
          <div className="container-wrapper">
            <div className="row-wrapper">
              <div className="ti-funnel-form">
                <div className="input-groups">
                  {steps.map((step, i) => {
                    const destinationSpecificTips = getDestinationSpecificTips(
                      destinationValues, tips, step
                    );

                    switch (step) {
                      case STEP_ENUM.INSURANCE_TYPE:
                        return (
                          <div
                            key={step}
                            className="input-group-wrapper"
                            ref={this.inputGroupWrapperRefCallback(step)}
                          >
                            {currentIndex >= i && (
                              <FunnelInputGroup
                                isActive={activeIndex === i}
                                isCurrent={currentIndex === i}
                                onClick={e => this.funnelInputGroupClickHandler(e, i)}
                              >
                                {tips[step] && tips[step].length > 0 && (
                                  <FunnelInputGroup.Header>
                                    {tips.map(tip => (
                                      <FunnelTip key={tip}>
                                        {tip}
                                      </FunnelTip>
                                    ))}
                                  </FunnelInputGroup.Header>
                                )}
                                {sentences.insuranceType1}
                                <InlineInput
                                  large={currentIndex === i}
                                  type="custom-panel"
                                  placeholder={inputPlaceholders.insuranceType}
                                  value={insuranceType && insuranceType.label}
                                  isCustomPanelPadded={false}
                                  customPanel={({ hideCustomPanel }) => (
                                    <SelectPanel
                                      onChange={({ label, value }) => {
                                        this.onInsuranceTypeChange({ label, value });
                                        hideCustomPanel();
                                      }}
                                      options={insuranceTypes}
                                      dataSelectorId="TI-funnel-insuranceType-SelectPanel"
                                    />
                                  )}
                                  dataSelectorId="TI-funnel-insuranceType"
                                  dataSelectorValue={insuranceType?.value}
                                />
                                {sentences.insuranceType2}
                              </FunnelInputGroup>
                            )}
                          </div>
                        );
                      case STEP_ENUM.DESTINATIONS:
                        return (
                          <div
                            key={step}
                            className="input-group-wrapper"
                            ref={this.inputGroupWrapperRefCallback(step)}
                          >
                            {currentIndex >= i && (
                              <FunnelInputGroup
                                isActive={activeIndex === i || isShowDestinationInputsError}
                                isCurrent={currentIndex === i}
                                onClick={e => this.funnelInputGroupClickHandler(e, i)}
                              >
                                {tips[step] && tips[step].length > 0 && (
                                  <FunnelInputGroup.Header>
                                    {tips[step].map(tip => (
                                      <FunnelTip key={tip}>
                                        {tip}
                                      </FunnelTip>
                                    ))}
                                  </FunnelInputGroup.Header>
                                )}
                                <div className="destination-funnel">
                                  {sentences.destinations1}
                                  {destinations.map(({ label = '', isValueFromSuggestion = false }, j) => (
                                    <span
                                      className="destination-input"
                                      data-delimiter={j === destinations.length - 2
                                        ? sentences.destinationsDelimiterLast
                                        : sentences.destinationsDelimiter}
                                      // TODO(victor): cannot use value as key,
                                      // being forced to use index
                                      // eslint-disable-next-line react/no-array-index-key
                                      key={j}
                                    >
                                      <InlineInput
                                        ref={this.inlineInputRefCallback('destination')}
                                        large={currentIndex === i}
                                        type="suggestion-only"
                                        placeholder={(j === 0)
                                          ? inputPlaceholders.destinationsSingle
                                          : inputPlaceholders.destinationsMulti}
                                        value={label}
                                        isValueFromSuggestion={isValueFromSuggestion}
                                        onChange={this.onDestinationChange(j)}
                                        onFocus={this.onDestinationFocus(j)}
                                        onBlur={this.onDestinationBlur(j)}
                                        isShowRemoveButton={isValueFromSuggestion}
                                        onRemoveButtonClick={this.onRemoveDestination(j)}
                                        suggestions={unusedDestinationSuggestions}
                                        dataSelectorId={`TI-funnel-destination-input-${j}`}
                                      />
                                    </span>
                                  ))}
                                  {isShowDestinationInputsError && (
                                    <Paragraph>
                                      <span className="input-validation-wrapper">
                                        {inputErrorMsg.destinationsSingle}
                                      </span>
                                    </Paragraph>
                                  )}
                                </div>
                                {isOneOfDestinationInputsFocused && (
                                  <div className="featured-destination-suggestions">
                                    {_.chunk(unusedFeaturedDestinationSuggestions, 3)
                                      .map((chunk, idx) => (
                                        // eslint-disable-next-line
                                        <div key={idx}>
                                          {chunk.map(({ label, value }, j) => (
                                            <span
                                              className="featured-destination-suggestion"
                                              key={value}
                                              data-selector={`TI-funnel-featured-destination-suggestion-${j}`}
                                            >
                                              <Button
                                                small
                                                tertiary
                                                noMinWidth
                                                onMouseDown={this
                                                  .onFeaturedDestinationSuggestionClick({
                                                    label,
                                                    value,
                                                  })}
                                                dataSelectorId={`TI-funnel-featured-destination-suggestion-button-${j}`}
                                              >
                                                {label || value}
                                              </Button>
                                            </span>
                                          ))}
                                        </div>
                                      ))}
                                  </div>
                                )}
                              </FunnelInputGroup>
                            )}
                          </div>
                        );
                      case STEP_ENUM.TRAVELLERS:
                        return (
                          <div
                            key={step}
                            className="input-group-wrapper"
                            ref={this.inputGroupWrapperRefCallback(step)}
                          >
                            {currentIndex >= i && (
                              <FunnelInputGroup
                                isActive={activeIndex === i}
                                isCurrent={currentIndex === i}
                                onClick={e => this.funnelInputGroupClickHandler(e, i)}
                              >
                                {tips[step] && tips[step].length > 0 && (
                                  <FunnelInputGroup.Header>
                                    {tips[step].map(tip => (
                                      <FunnelTip key={tip}>
                                        {tip}
                                      </FunnelTip>
                                    ))}
                                  </FunnelInputGroup.Header>
                                )}
                                {sentences.travellers1}
                                {travellers.map(({ relationship = {}, dateOfBirth = '', dateOfBirthValid = false }, j) => (
                                  <span
                                    className="traveller-input"
                                    data-delimiter={j === travellers.length - 2
                                      ? sentences.travellersDelimiterLast
                                      : sentences.travellersDelimiter}
                                    // TODO(victor): cannot use value as key,
                                    // being forced to use index
                                    // eslint-disable-next-line react/no-array-index-key
                                    key={j}
                                  >
                                    <InlineInput
                                      large={currentIndex === i}
                                      type="custom-panel"
                                      placeholder={(j === 0)
                                        ? inputPlaceholders.travellersSingle
                                        : inputPlaceholders.travellersMulti}
                                      value={!_.isEmpty(relationship) ? relationship.label : ''}
                                      isShowRemoveButton={!_.isEmpty(relationship)
                                        && relationship.value !== RELATIONSHIP_ENUM.SELF}
                                      onRemoveButtonClick={this.onRemoveTraveller(j)}
                                      onCustomPanelOutsideClick={this
                                        .onCustomPanelOutsideClick(j)}
                                      overrideCustomPanelOutsideClick={relationship.value
                                        && !dateOfBirthValid}
                                      showCustomPanel={relationship.value
                                        && currentIndex === i
                                        && !dateOfBirthValid}
                                      customPanelClassName={activeIndex !== i
                                        ? 'custom-panel-hidden'
                                        : null}
                                      disabled={!this.allTravelersAreValid()}
                                      customPanel={({ hideCustomPanel }) => (relationship.value
                                        ? (
                                          <DobPanel
                                            initialValue={dateOfBirth}
                                            isForcedShowError={forceDobPanelShowError}
                                            minAge={this
                                              .getAgeLimitsFromRelationship(relationship).min}
                                            maxAge={this
                                              .getAgeLimitsFromRelationship(relationship).max}
                                            dobMessages={this
                                              .getDOBPanelMessages(relationship).dobMessages}
                                            onConfirm={(value) => {
                                              this.onDateOfBirthChange(
                                                j,
                                                unusedRelationshipSelections
                                              )(value);
                                              this.setState({ forceDobPanelShowError: false });
                                              hideCustomPanel();
                                            }}
                                            onDateOfBirthValidChange={(value) => {
                                              this.onDateOfBirthValidChange(
                                                j,
                                                unusedRelationshipSelections
                                              )(value);
                                            }}
                                            dataSelectorId="TI-funnel-traveller-dob-input"
                                            dataSelectorValue={relationship?.value}
                                          />
                                        ) : (
                                          <RelationshipPanel
                                            onChange={({ label, value }) => {
                                              this.setState({ forceDobPanelShowError: false });
                                              this.onRelationshipChange(j)({ label, value });
                                            }}
                                            options={unusedRelationshipSelections}
                                          />
                                        )
                                      )}
                                      dataSelectorId={`TI-funnel-traveller-input-${relationship?.value}`}
                                      dataSelectorValue={`${dateOfBirth}`}
                                    />
                                  </span>
                                ))}
                              </FunnelInputGroup>
                            )}
                          </div>
                        );
                      case STEP_ENUM.START_DATE_AND_END_DATE:
                        return (
                          <div
                            key={step}
                            className="input-group-wrapper"
                            ref={this.inputGroupWrapperRefCallback(step)}
                          >
                            {currentIndex >= i && (
                              <FunnelInputGroup
                                isActive={activeIndex === i}
                                isCurrent={currentIndex === i}
                                onClick={e => this.funnelInputGroupClickHandler(e, i)}
                              >
                                {destinationSpecificTips && destinationSpecificTips.length > 0 && (
                                  <FunnelInputGroup.Header>
                                    {destinationSpecificTips.map(tip => (
                                      <FunnelTip key={tip}>
                                        {tip}
                                      </FunnelTip>
                                    ))}
                                  </FunnelInputGroup.Header>
                                )}
                                {sentences.startDateAndEndDate1}
                                <InlineInput
                                  ref={this.inlineInputRefCallback('startDate')}
                                  large={currentIndex === i}
                                  type="custom-panel"
                                  placeholder={inputPlaceholders.startDate}
                                  value={getTiFunnelTripDateFormat(startDate, locale)}
                                  isCustomPanelPadded={false}
                                  dayPicker={true}
                                  customPanel={({ hideCustomPanel }) => (
                                    <DatePicker
                                      date={startDate ? moment(startDate) : null}
                                      noBorder
                                      focused
                                      isOutsideRange={date => !validateStartDate(
                                        date, singlePlanStartDayLimit
                                      )}
                                      onDateChange={(date) => {
                                        this.onSinglePlanStartDateChange(date);
                                        hideCustomPanel();
                                      }}
                                    />
                                  )}
                                  dataSelectorId="TI-funnel-startDate"
                                  dataSelectorValue={startDate}
                                />
                                {sentences.startDateAndEndDate2}
                                <InlineInput
                                  ref={this.inlineInputRefCallback('endDate')}
                                  large={currentIndex === i}
                                  type="custom-panel"
                                  placeholder={inputPlaceholders.endDate}
                                  value={getTiFunnelTripDateFormat(endDate, locale)}
                                  isCustomPanelPadded={false}
                                  dayPicker={true}
                                  customPanel={({ hideCustomPanel }) => (
                                    <DatePicker
                                      date={endDate ? moment(endDate) : null}
                                      noBorder
                                      focused
                                      initialVisibleMonth={() => (
                                        (endDate && moment(endDate))
                                        || (startDate && moment(startDate))
                                        || moment()
                                      )}
                                      isOutsideRange={date => !validateEndDate(
                                        date, startDate, singlePlanEndDayLimit
                                      )}
                                      onDateChange={(date) => {
                                        this.setState({
                                          endDate: date.format(DATE_FORMAT),
                                        }, this.validate);
                                        hideCustomPanel();
                                      }}
                                    />
                                  )}
                                  dataSelectorId="TI-funnel-endDate"
                                />
                              </FunnelInputGroup>
                            )}
                          </div>
                        );
                      case STEP_ENUM.START_DATE:
                        return (
                          <div
                            key={step}
                            className="input-group-wrapper"
                            ref={this.inputGroupWrapperRefCallback(step)}
                          >
                            {currentIndex >= i && (
                              <FunnelInputGroup
                                isActive={activeIndex === i}
                                isCurrent={currentIndex === i}
                                onClick={e => this.funnelInputGroupClickHandler(e, i)}
                              >
                                {destinationSpecificTips && destinationSpecificTips.length > 0 && (
                                  <FunnelInputGroup.Header>
                                    {destinationSpecificTips.map(tip => (
                                      <FunnelTip key={tip}>
                                        {tip}
                                      </FunnelTip>
                                    ))}
                                  </FunnelInputGroup.Header>
                                )}
                                {sentences.startDate1}
                                <InlineInput
                                  ref={this.inlineInputRefCallback('startDateAnnual')}
                                  large={currentIndex === i}
                                  type="custom-panel"
                                  placeholder={inputPlaceholders.startDate}
                                  value={getTiFunnelTripDateFormat(startDate, locale)}
                                  isCustomPanelPadded={false}
                                  dayPicker={true}
                                  customPanel={({ hideCustomPanel }) => (
                                    <DatePicker
                                      noBorder
                                      focused
                                      date={startDate ? moment(startDate) : null}
                                      isOutsideRange={date => !validateStartDate(
                                        date, annualPlanStartDayLimit
                                      )}
                                      onDateChange={(date) => {
                                        this.setState({ startDate: date.format('YYYY-MM-DD') }, this.validate);
                                        hideCustomPanel();
                                      }}
                                    />
                                  )}
                                  dataSelectorId="TI-funnel-startDate"
                                />
                              </FunnelInputGroup>
                            )}
                          </div>
                        );
                      case STEP_ENUM.REGION:
                        return (
                          <div
                            key={step}
                            className="input-group-wrapper"
                            ref={this.inputGroupWrapperRefCallback(step)}
                          >
                            {currentIndex >= i && (
                              <FunnelInputGroup
                                isActive={activeIndex === i}
                                isCurrent={currentIndex === i}
                                onClick={e => this.funnelInputGroupClickHandler(e, i)}
                              >
                                {tips[step] && tips[step].length > 0 && (
                                  <FunnelInputGroup.Header>
                                    {tips[step].map(tip => (
                                      <FunnelTip key={tip}>
                                        {tip}
                                      </FunnelTip>
                                    ))}
                                  </FunnelInputGroup.Header>
                                )}
                                {sentences.regionCoverIn}
                                <InlineInput
                                  large={currentIndex === i}
                                  type="custom-panel"
                                  placeholder={inputPlaceholders.region}
                                  value={region && region.label}
                                  isCustomPanelPadded={false}
                                  customPanel={({ hideCustomPanel }) => (
                                    <SelectPanel
                                      onChange={({ label, value }) => {
                                        this.onRegionChange({ label, value });
                                        hideCustomPanel();
                                      }}
                                      options={regions}
                                    />
                                  )}
                                  dataSelectorValue={region && region.value}
                                  dataSelectorId="TI-funnel-region"
                                />

                              </FunnelInputGroup>
                            )}
                          </div>
                        );
                      case STEP_ENUM.COVERAGES:
                        return (
                          <div
                            key={step}
                            className="input-group-wrapper"
                            ref={this.inputGroupWrapperRefCallback(step)}
                          >
                            {currentIndex >= i && (
                              <FunnelInputGroup
                                isActive={activeIndex === i}
                                isCurrent={currentIndex === i}
                                onClick={e => this.funnelInputGroupClickHandler(e, i)}
                              >
                                {tips[step] && tips[step].length > 0 && (
                                  <FunnelInputGroup.Header>
                                    {tips[step].map(tip => (
                                      <FunnelTip key={tip}>
                                        {tip}
                                      </FunnelTip>
                                    ))}
                                  </FunnelInputGroup.Header>
                                )}
                                {sentences.additionalCoverages1}
                                <div className="coverage-selections">
                                  {coverageSelections.map(({ label, value, icon }, idx) => (
                                    <div className="coverage-selection" key={value} data-selector-type="TI-funnel-coverage" data-selector-index={idx}>
                                      <FunnelSelectionButton
                                        leftIconName={icon}
                                        isSelected={_.find(additionalCoverages, { value })}
                                        onClick={this.onCoverageSelectionButtonClick({
                                          label,
                                          value,
                                        })}
                                        dataSelectorId="TI-funnel-coverage"
                                        dataSelectorValue={value}
                                      >
                                        {label || value}
                                      </FunnelSelectionButton>
                                    </div>
                                  ))}
                                </div>
                              </FunnelInputGroup>
                            )}
                          </div>
                        );
                      default:
                        return null;
                    }
                  })}
                </div>
                <div>
                  <Button
                    dataSelectorId="next"
                    className="next-button"
                    primary
                    onClick={this.onNextButtonClick}
                    disabled={!isValid}
                  >
                    {currentIndex === steps.length - 1
                      ? dictionary.showMyResults
                      : dictionary.next}
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </Fragment>
    );
  }
}

TravelInsuranceFunnel.propTypes = {
  initialFunnelQuery: shape({}),
  onSubmit: func,
  history: shape({}).isRequired,
  location: shape({}).isRequired,

  // App config
  match: shape({}),
  localisation: shape({}),
  language: string.isRequired,
  topBanner: shape({ desktopImageKey: string, mobileImageKey: string }),
  providerImages: arrayOf(shape({})),
  promotionalBanners: arrayOf(shape({})),
  dictionary: shape({}),
  typeSelectionPanel: shape({}),
  sentences: shape({}),
  tips: shape({}),
  inputPlaceholders: shape({}),
  inputErrorMsg: shape({}),
  insuranceTypes: arrayOf(shape({})),
  featuredDestinationSuggestions: arrayOf(shape({})),
  singlePlanRelationashipSelections: arrayOf(shape({})),
  annualPlanRelationashipSelections: arrayOf(shape({})),
  coverageSelections: arrayOf(shape({})),
  countryOptions: shape({}).isRequired,
  ageLimits: shape({}),
  rightSideImg: shape({}).isRequired,
  singlePlanEndDayLimit: number.isRequired,
  singlePlanStartDayLimit: number.isRequired,
  annualPlanStartDayLimit: number.isRequired,
  tiRoutes: shape({}).isRequired,
  funnelPathname: string.isRequired,
  singlePlanSteps: arrayOf(string),
  annualPlanSteps: arrayOf(string),
  regions: arrayOf(shape({})),
  excludedDestinations: arrayOf(string),
  onPromotionalBannerClick: func,
  latestNews: shape({}),
  faqs: shape({}),
  tiShortDisclaimer: string,
  tiLongDisclaimer: string,
  video: shape({}),
};

TravelInsuranceFunnel.defaultProps = {
  initialFunnelQuery: null,
  onSubmit: undefined,

  // App config
  match: {},
  localisation: {},
  topBanner: { desktopImageKey: '', mobileImageKey: '' },
  promotionalBanners: [],
  dictionary: {},
  typeSelectionPanel: {},
  sentences: {},
  tips: {},
  inputPlaceholders: {},
  inputErrorMsg: {},
  insuranceTypes: [],
  featuredDestinationSuggestions: [],
  singlePlanRelationashipSelections: [],
  annualPlanRelationashipSelections: [],
  coverageSelections: [],
  ageLimits: {},
  singlePlanSteps: [],
  annualPlanSteps: [],
  regions: [],
  excludedDestinations: [],
  onPromotionalBannerClick: () => {},
};

export default withRouter(withTracking(withApplicationConfig(TravelInsuranceFunnel, ['ti-funnel', 'optionKeys', 'languages'])));
